import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import MainCard from "../../../../../../components/MainCard";

import { useTheme } from "@mui/material/styles";
import {
  SnackbarProps,
  Stack,
  Tooltip,
  Typography,
  IconButton,
  Button,
  Chip,
} from "@mui/material";
import _debounce from "lodash/debounce";

import { ColumnDef } from "@tanstack/react-table";

import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import Loader from "../../../../../../components/Loader";
import { FormattedMessage } from "react-intl";
import { USERAXIOS } from "../../../../../../api/userAxios";
import ReusableSimpleTable from "../../../../../../components/ReusableComponent/ReusableSimpleTable";
import { openSnackbar } from "../../../../../../api/snackbar";
import ReusableDeleteDialog from "../../../../../../components/ReusableComponent/ReusableDeleteDialog";
import { EyeOutlined, CloseOutlined } from "@ant-design/icons";
import ExpandingOrderItemDetail from "./ExpandingOrderItemDetail";
import AddEditOrderItemForm from "./AddEditOrderItemForm";
import { OrderItem } from "../../../../../../types/orders-managment/orders";
import { Box } from "@mui/system";

const OrderItems = ({ orderId }: { orderId?: number }) => {
  const theme = useTheme();
  const [open, setOpen] = useState<boolean>(false);

  const [customerModal, setCustomerModal] = useState<boolean>(false);
  const [expandedRowId, setExpandedRowId] = useState<string | number | null>(
    null
  );
  const [selected, setSelected] = useState<OrderItem | null>(null);

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<OrderItem[]>([]);

  const [isDeleting, setIsDeleting] = useState(false);

  const handleClose = () => {
    setOpen(!open);
  };

  const columns = useMemo<ColumnDef<OrderItem>[]>(
    () => [
      {
        header: () => <FormattedMessage id="ID" />,
        accessorKey: "id",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography variant="subtitle2">{row.original.id}</Typography>
          </Stack>
        ),
      },
      {
        header: () => <FormattedMessage id="Packable Type" />,
        accessorKey: "packable_type",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Typography variant="subtitle2">
            {row.original.packable_type}
          </Typography>
        ),
      },
      {
        header: () => <FormattedMessage id="Package Duration" />,
        accessorKey: "package_duration",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Typography variant="subtitle2">
            {row.original.package_duration}
          </Typography>
        ),
      },

      {
        header: () => <FormattedMessage id="Package Validity" />,
        accessorKey: "package_validity",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Typography variant="subtitle2">
            {row.original.package_validity} days
          </Typography>
        ),
      },
      {
        header: () => <FormattedMessage id="Price" />,
        accessorKey: "price",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Typography variant="subtitle2">
            {row.original.price.toLocaleString()}
          </Typography>
        ),
      },
      {
        header: () => <FormattedMessage id="Start Date" />,
        accessorKey: "start_date",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Typography variant="subtitle2">{row.original.start_date}</Typography>
        ),
      },
      {
        header: () => <FormattedMessage id="End Date" />,
        accessorKey: "end_date",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Typography variant="subtitle2">{row.original.end_date}</Typography>
        ),
      },
      {
        header: () => <FormattedMessage id="Show in Best Place" />,
        accessorKey: "meta.show_in_best_place",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Chip
            color={row.original.meta.show_in_best_place ? "success" : "error"}
            label={
              row.original.meta.show_in_best_place ? (
                <FormattedMessage id="Yes" />
              ) : (
                <FormattedMessage id="No" />
              )
            }
            size="small"
            variant="filled"
          />
        ),
      },
      // {
      //   header: () => <FormattedMessage id="Vacancy Count" />,
      //   accessorKey: "meta.vacancy_count",
      //   meta: { className: "cell-center" },
      //   cell: ({ row }) => (
      //     <Typography variant="subtitle2">
      //       {row.original.meta.vacancy_count}
      //     </Typography>
      //   ),
      // },
      {
        header: () => <FormattedMessage id="Actions" />,
        accessorKey: "actions",
        meta: { className: "cell-center" },
        cell: ({ row }) => {
          const isExpanded = String(expandedRowId) === String(row.original.id);
          const collapseIcon = isExpanded ? (
            <CloseOutlined style={{ color: theme.palette.error.main }} />
          ) : (
            <EyeOutlined />
          );
          return (
            <Stack direction="row">
              <Tooltip title={isExpanded ? "close details" : "view details"}>
                <IconButton
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpandedRowId(
                      isExpanded ? null : String(row.original.id)
                    );
                  }}
                  sx={{ "&:hover": { backgroundColor: "transparent" } }}
                >
                  {collapseIcon}
                </IconButton>
              </Tooltip>
              {/* <Tooltip title="Edit">
                <IconButton
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelected(row.original);
                    setCustomerModal(true);
                  }}
                  sx={{ "&:hover": { backgroundColor: "transparent" } }}
                >
                  <EditOutlined />
                </IconButton>
              </Tooltip> */}
              <Tooltip title="Delete">
                <IconButton
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClose();
                    setSelected(row.original);
                  }}
                >
                  <DeleteOutlined />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        },
      },
    ],
    [theme, expandedRowId]
  );

  const handleDelete = async () => {
    if (!selected?.id) return;
    setIsDeleting(true);
    try {
      await USERAXIOS.delete(`/order_items/${selected?.id}`);
      setResult((prevResults: any[]) =>
        prevResults.filter((item) => item.id !== selected?.id)
      );
      openSnackbar({
        open: true,
        message: "Order items deleted successfully",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        close: false,
        alert: {
          color: "success",
        },
      } as SnackbarProps);
      if (handleClose) handleClose();
    } catch (error: any) {
      openSnackbar({
        open: true,
        message:
          error?.response?.data?.message || "Failed to delete order items",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "error",
        },
      } as SnackbarProps);
    } finally {
      setIsDeleting(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await USERAXIOS.get(`/order_items`);
      const { data, meta } = response.data;
      setResult(data);
    } catch (error) {
      openSnackbar({
        open: true,
        message: "Error fetching articles",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "error",
        },
      } as SnackbarProps);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <MainCard
          title="Order items"
          // headerActions={
          //   <Button
          //     variant="contained"
          //     color="primary"
          //     onClick={() => {
          //       setCustomerModal(true);
          //       setSelected(null);
          //     }}
          //   >
          //     Add Course
          //   </Button>
          // }
        >
          <ReusableSimpleTable
            data={result}
            columns={columns}
            renderDetailPanel={({ row }: any) => (
              <ExpandingOrderItemDetail data={row.original} />
            )}
            expandedRowId={expandedRowId}
            setExpandedRowId={setExpandedRowId}
          />
        </MainCard>
      )}
      <ReusableDeleteDialog
        title={selected?.packable.note}
        open={open}
        handleClose={handleClose}
        handleDelete={handleDelete}
        isDeleting={isDeleting}
      />

      {/* {customerModal && (
        <AddEditOrderItemForm
          setResult={setResult}
          item={selected}
          open={customerModal}
          setOpen={setCustomerModal}
          orderId={orderId}
        />
      )} */}
    </>
  );
};

export default OrderItems;
